import type { WidgetLoginResponse, WidgetSettingsResponse } from '@teo/widget-shared';
import type { ScriptTagConfig } from '../config/parse-script-config.js';
import { getVisitorId } from '../fingerprint/get-visitor-id.js';
import {
  createWidgetSession,
  clearWidgetSession,
  setWidgetSession,
  type WidgetSession,
} from '../session/widget-session.js';
import {
  createStoredWidgetAuth,
  readStoredWidgetAuth,
  storedAuthToLoginResponse,
  writeStoredWidgetAuth,
  type StoredWidgetAuth,
} from '../session/session-storage.js';
import { fetchWidgetSettings, WidgetSettingsError } from './fetch-settings.js';
import { widgetLogin } from './widget-login.js';
import { refreshWidgetSessionThread } from '../session/refresh-session-thread.js';
import { getWidgetSession } from '../session/widget-session.js';
import { publishWidgetSettings } from './publish-widget-settings.js';

export type WidgetBootstrapOptions = {
  /** Skip localStorage session cache (login test page, forced refresh). */
  forceLogin?: boolean;
};

export type WidgetBootstrapResult = {
  settings: WidgetSettingsResponse;
  session: WidgetSession;
  login: WidgetLoginResponse;
  /** `true` when auth was restored from localStorage (settings still fetched fresh). */
  fromCache: boolean;
};

function applySession(
  login: Omit<WidgetLoginResponse, 'settings'>,
  config: ScriptTagConfig,
  visitorId: string,
): WidgetSession {
  const session = createWidgetSession(login, config, visitorId);
  setWidgetSession(session);
  return session;
}

async function fetchFreshSettings(
  config: ScriptTagConfig,
): Promise<WidgetSettingsResponse> {
  return fetchWidgetSettings(config);
}

async function bootstrapWithLogin(
  config: ScriptTagConfig,
  visitorId: string,
  apiBase: string,
): Promise<{ login: WidgetLoginResponse; fromCache: boolean }> {
  const login = await widgetLogin(config, visitorId);

  // Persist for mock too — thread_id patches after first send must survive,
  // and re-engage dwell depends on live thread becoming visible to the UI.
  if (config.slug) {
    writeStoredWidgetAuth(
      createStoredWidgetAuth(login, config.slug, visitorId, apiBase),
    );
  }

  return { login, fromCache: false };
}

async function resolveAuth(
  config: ScriptTagConfig,
  visitorId: string,
  apiBase: string,
  options?: WidgetBootstrapOptions,
): Promise<{
  login: Omit<WidgetLoginResponse, 'settings'>;
  fromCache: boolean;
}> {
  if (
    !options?.forceLogin &&
    !config.mock &&
    config.slug
  ) {
    const cached: StoredWidgetAuth | null = readStoredWidgetAuth(
      config.slug,
      visitorId,
      apiBase,
    );
    if (cached) {
      return { login: storedAuthToLoginResponse(cached), fromCache: true };
    }
  }

  const { login } = await bootstrapWithLogin(config, visitorId, apiBase);
  return {
    login: {
      id: login.id,
      company_id: login.company_id,
      module_type: login.module_type,
      token: login.token,
      thread_id: login.thread_id,
    },
    fromCache: false,
  };
}

/**
 * Authenticate (or restore cached auth), then always load settings from
 * `GET /api/get-modules-settings?company_id=…`.
 */
export async function bootstrapWidget(
  config: ScriptTagConfig,
  options?: WidgetBootstrapOptions,
): Promise<WidgetBootstrapResult> {
  const visitorId = await getVisitorId();
  const apiBase = config.apiBase || window.location.origin;

  const { login: auth, fromCache } = await resolveAuth(
    config,
    visitorId,
    apiBase,
    options,
  );
  const session = applySession(auth, config, visitorId);

  // Cached auth may have thread_id: null — refresh from login before WS/history.
  if (
    !config.mock &&
    config.slug &&
    (session.threadId == null || Number(session.threadId) <= 0)
  ) {
    await refreshWidgetSessionThread(apiBase);
  }

  const activeSession = getWidgetSession() ?? session;

  try {
    const settings = await fetchFreshSettings(config);
    publishWidgetSettings(settings);
    return {
      settings,
      session: activeSession,
      login: { ...auth, settings, thread_id: activeSession.threadId },
      fromCache,
    };
  } catch (error) {
    if (fromCache && error instanceof WidgetSettingsError && error.status === 401) {
      clearWidgetSession(config.slug ?? undefined);
      return bootstrapWidget(config, { forceLogin: true });
    }
    throw error;
  }
}
